import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { isActive } from "@/App";
import {
  Sparkles, MessagesSquare, PenLine, Repeat, Waypoints, CalendarDays,
  MonitorPlay, ReceiptText,
  Check, ChevronDown, ArrowRight, Zap,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginRedirect = () => {
  window.location.href = `${window.location.origin}/api/auth/google/login`;
};

const IMG = {
  mockup: "https://customer-assets-jt897jd0.emergentagent.net/job_kreator-ai/artifacts/6fmsrbrg_ChatGPT%20Image%20Aug%2031%2C%202026%2C%2003_00_03%20PM.png",
};

// Subtle dot-grid paper texture (used as a "bookend" on Hero + closing CTA)
const dotGrid = {
  backgroundImage: "radial-gradient(rgba(36,28,23,0.05) 1.2px, transparent 1.2px)",
  backgroundSize: "22px 22px",
};

const STANDARD_FEATURES_TOP = [
  { icon: Sparkles, title: "Identitas Kreator", desc: "Racik DNA Personal Brand kamu — positioning, tone, dan pilar konten yang jadi fondasi semua karya." },
  { icon: MessagesSquare, title: "Brainstorming", desc: "Partner diskusi AI yang bantu asah premis kasar jadi ide konten tajam sesuai gaya kamu." },
  { icon: MonitorPlay, title: "Teleprompter", desc: "Baca naskah lancar tanpa lepas tatapan dari kamera — kecepatan, ukuran teks, sampai mode cermin bisa diatur." },
  { icon: Repeat, title: "Recreate Konten", desc: "Upload video/audio favorit, AI tiru pola penyampaiannya untuk topik baru kamu." },
];

const PAINS = [
  { n: "01", title: "Kehabisan ide", desc: "Tiap mau bikin konten, blank. Scroll sana-sini nyari inspirasi malah makin bingung." },
  { n: "02", title: "Script nggak engaging", desc: "Udah nulis panjang-panjang tapi hook-nya lemah, penonton kabur di 3 detik pertama." },
  { n: "03", title: "Konten nggak konsisten", desc: "Kadang rajin, kadang hilang berminggu-minggu. Algoritma & audiens jadi lupa sama kamu." },
];

const TESTIMONIALS = [
  { name: "Harum", handle: "@harumwijsman", img: "https://customer-assets-jt897jd0.emergentagent.net/job_kreator-ai/artifacts/srgfwlrv_Harum.jpg", quote: "Biasanya kalau mau bikin konten aku suka stuck mulai dari mana. Studio Kreator bikin prosesnya jauh lebih gampang karena ide yang masih random bisa langsung dikembangkan jadi konsep yang lebih jelas dan sesuai sama personal brand aku." },
  { name: "Mutiara Annisa", handle: "@mutiaranniisa", img: "https://customer-assets-jt897jd0.emergentagent.net/job_kreator-ai/artifacts/e8neu6za_Mutiara.jpg", quote: "Yang aku suka, Studio Kreator nggak cuma kasih ide konten, tapi juga bantu mikirin angle, hook, sampai script-nya. Jadi aku bisa lebih fokus ke eksekusi tanpa harus menghabiskan waktu lama buat mikirin mau bikin konten apa." },
  { name: "Ari Kurnia", handle: "@arikurniawibawa", img: "https://customer-assets-jt897jd0.emergentagent.net/job_kreator-ai/artifacts/hobuhq89_Ari.jpg", quote: "Studio Kreator ngebantu aku melihat konten dari perspektif yang lebih strategis. Bukan sekadar bikin konten supaya rutin posting, tapi juga tahu konten ini dibuat untuk awareness, membangun trust, atau mendorong conversion." },
];

const FAQS = [
  { q: "Apa itu Studio Kreator?", a: "AI creative assistant khusus content creator (YouTube, TikTok, Instagram, X) untuk bikin personal brand, ide, script, sampai jadwal konten — semua nyambung sama gaya kamu." },
  { q: "Apakah hasilnya sesuai gaya bicara saya?", a: "Iya. Semua fitur membawa konteks Identitas Kreator kamu, jadi output-nya konsisten sama niche, audiens, dan tone kamu — bukan saran generik." },
  { q: "Metode pembayaran apa yang didukung?", a: "Pembayaran via QRIS (scan pakai e-wallet/m-banking apapun) atau Virtual Account, diproses aman lewat Paywuz." },
  { q: "Apakah bisa berhenti kapan saja?", a: "Bisa. Langganan aktif sampai masa berlakunya habis, dan kamu bebas memutuskan untuk memperpanjang atau tidak." },
  { q: "Apakah ada garansi?", a: <>Pembayaran langganan yang sudah berhasil diproses bersifat non-refundable, kecuali terjadi kesalahan teknis dari pihak kami — misalnya biaya terpotong dua kali untuk satu transaksi, atau akses gagal terbuka setelah pembayaran berhasil dikonfirmasi. Kalau kamu mengalami masalah seperti itu, hubungi kami di <a href="mailto:halo@studiokreator.id" className="text-sk-brand font-semibold hover:underline">halo@studiokreator.id</a> dengan bukti transaksi, dan kami akan menindaklanjuti maksimal dalam 3 hari kerja. Untuk detail lengkap, lihat <Link to="/syarat-layanan" className="text-sk-brand font-semibold hover:underline">Syarat Layanan</Link>.</> },
];

function Tag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-sk-line bg-sk-surface/70 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sk-muted backdrop-blur-sm">
      <span className="h-1.5 w-1.5 rounded-full bg-sk-brand" />
      {children}
    </span>
  );
}

// gray placeholder line, width in %
function Line({ w = "100%" }) {
  return <div className="h-2.5 rounded-full bg-sk-line/90" style={{ width: w }} />;
}

function DraftBlock({ label, widths }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-wider text-sk-brand">{label}</p>
      <div className="space-y-1.5">
        {widths.map((w, i) => <Line key={i} w={w} />)}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);

  const scrollToPricing = () => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const subscribe = (plan) => {
    localStorage.setItem("sk_plan", plan);
    if (user) navigate(`/checkout?plan=${plan}`);
    else loginRedirect();
  };
  const goApp = () => {
    if (user && isActive(user)) navigate("/dashboard");
    else if (user) navigate("/checkout");
    else loginRedirect();
  };

  return (
    <div className="min-h-screen bg-sk-bg text-sk-cream">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-sk-bg/85 backdrop-blur-md border-b border-sk-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src="/logo.png" alt="Studio Kreator" className="h-8 w-auto" />
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => scrollTo("features")} data-testid="nav-features-link" className="hidden sm:inline text-sm font-semibold text-sk-muted hover:text-sk-cream px-3 py-2 transition-colors">Fitur</button>
            <button onClick={scrollToPricing} data-testid="nav-pricing-link" className="hidden sm:inline text-sm font-semibold text-sk-muted hover:text-sk-cream px-3 py-2 transition-colors">Harga</button>
            <button onClick={() => scrollTo("faq")} data-testid="nav-faq-link" className="hidden sm:inline text-sm font-semibold text-sk-muted hover:text-sk-cream px-3 py-2 transition-colors">FAQ</button>
            <button onClick={goApp} data-testid="nav-login-btn" className="rounded-full bg-sk-cream text-sk-ink font-bold text-sm px-5 py-2.5 ml-1 hover:bg-sk-cream/90 transition-colors">{user ? "Buka App" : "Masuk"}</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-sk-line" style={dotGrid}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-20 relative grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Tag>Buat kreator Indonesia</Tag>
            <h1 className="mt-5 font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-sk-cream">
              Asisten kreatif AI yang bikin konten <span className="text-sk-brand">terasa kamu banget.</span>
            </h1>
            <p className="mt-6 text-sk-muted text-base sm:text-lg max-w-xl leading-relaxed">
              Studio Kreator bantu content creator dari nol ide sampai script siap tayang — personal brand, brainstorming, penulisan, sampai penjadwalan, semua nyambung sama gaya kamu.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <button onClick={scrollToPricing} data-testid="hero-cta" className="flex items-center gap-2 rounded-full bg-sk-cream text-sk-ink font-bold px-7 py-3.5 hover:-translate-y-0.5 transition-transform shadow-lg shadow-black/10">
                <Zap className="h-4 w-4 text-sk-brand" /> Mulai Sekarang
              </button>
              <button onClick={scrollToPricing} className="flex items-center gap-1.5 font-semibold text-sk-cream underline underline-offset-[6px] decoration-2 decoration-sk-brand/40 hover:decoration-sk-brand transition-colors">
                Lihat harga <ArrowRight className="h-4 w-4 text-sk-brand" />
              </button>
            </div>
          </motion.div>

          {/* Script card stack */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="relative mx-auto w-full max-w-sm lg:max-w-md pb-6 pr-4">
            {/* back card */}
            <div aria-hidden className="absolute inset-x-6 top-6 bottom-0 rounded-3xl border border-sk-line bg-white/55 rotate-[5deg] shadow-xl shadow-black/5" />
            {/* front card */}
            <div className="relative rounded-3xl border border-sk-line bg-white p-6 sm:p-7 rotate-[-2.5deg] hover:rotate-0 transition-transform duration-500 shadow-2xl shadow-black/10">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs font-semibold text-sk-muted">Draft Script</p>
                  <p className="text-sm font-bold text-sk-cream">Rutinitas pagi produktif</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sk-brand/12 border border-sk-brand/30 px-2.5 py-1 text-[10px] font-bold text-sk-brand">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sk-brand opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sk-brand" />
                  </span>
                  LIVE
                </span>
              </div>
              <div className="space-y-5">
                <DraftBlock label="Hook" widths={["92%", "68%"]} />
                <DraftBlock label="Isi" widths={["100%", "88%", "74%"]} />
                <DraftBlock label="CTA" widths={["60%"]} />
              </div>
            </div>
            {/* floating quote chip */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 }}
              className="absolute -bottom-2 -right-1 sm:right-0 max-w-[13rem] rounded-2xl border border-sk-line bg-white px-4 py-3 shadow-xl shadow-black/10">
              <div className="flex gap-0.5 text-sk-brand text-xs mb-1">{"★★★★★".split("").map((s, k) => <span key={k}>{s}</span>)}</div>
              <p className="text-xs text-sk-cream leading-snug font-medium">"Nyambung banget sama gaya bicara aku."</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem — editorial columns */}
      <section className="border-b border-sk-line bg-sk-surface2/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <Tag>Kenapa susah</Tag>
          <h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream max-w-2xl">Bikin konten itu melelahkan kalau sendirian.</h2>
          <div className="mt-12 grid md:grid-cols-3 md:divide-x divide-sk-line/70">
            {PAINS.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0 border-b border-sk-line/70 md:border-b-0 last:border-b-0">
                <p className="font-extrabold text-sk-brand/70 text-lg tracking-tight tabular-nums">{p.n}</p>
                <h3 className="mt-3 font-bold text-xl text-sk-cream">{p.title}</h3>
                <p className="text-sk-muted text-sm mt-2.5 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-sk-line bg-sk-bg scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <Tag>Fitur</Tag>
          <h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream max-w-2xl">Semua yang kamu butuh, dalam satu studio.</h2>
          <div className="grid grid-cols-1 min-[520px]:grid-cols-2 min-[860px]:grid-cols-4 grid-flow-dense gap-4 mt-12 auto-rows-auto min-[860px]:auto-rows-[1fr]">
            {/* Susun Script — featured big (2x2) */}
            <motion.div
              data-testid="feature-card-susun"
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              style={{ background: "#17110D" }}
              className="rounded-2xl p-6 sm:p-8 text-sk-ink flex flex-col min-[520px]:col-span-2 min-[860px]:row-span-2"
            >
              <span className="self-start inline-flex items-center rounded-full bg-sk-brand/15 text-sk-brand text-xs font-bold px-3 py-1">PALING SERING DIPAKAI</span>
              <div className="mt-6 min-[860px]:mt-auto min-[860px]:pt-8">
                <PenLine className="h-10 w-10 mb-4 text-sk-brand" strokeWidth={1.6} />
                <h3 className="font-extrabold text-2xl sm:text-3xl tracking-tight text-sk-ink">Susun Script</h3>
                <p className="text-base mt-3 leading-relaxed text-sk-ink/75 max-w-md">Hook, Foreshadow, Story (isi konten), dan Payoff (CTA) tersusun nyambung jadi satu naskah siap tayang.</p>
              </div>
            </motion.div>

            {/* Standard cards — top-right cluster */}
            {STANDARD_FEATURES_TOP.map((f, i) => (
              <motion.div key={f.title} data-testid={`feature-card-${f.title}`} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="rounded-2xl p-6 bg-sk-cream text-sk-ink flex flex-col">
                <f.icon className="h-8 w-8 mb-4 text-sk-brand" strokeWidth={1.6} />
                <h3 className="font-bold text-lg text-sk-ink">{f.title}</h3>
                <p className="text-sm mt-2 leading-relaxed text-sk-ink/70">{f.desc}</p>
              </motion.div>
            ))}

            {/* Peta Konversi — standard */}
            <motion.div data-testid="feature-card-Peta Konversi" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="rounded-2xl p-6 bg-sk-cream text-sk-ink flex flex-col">
              <Waypoints className="h-8 w-8 mb-4 text-sk-brand" strokeWidth={1.6} />
              <h3 className="font-bold text-lg text-sk-ink">Peta Konversi</h3>
              <p className="text-sm mt-2 leading-relaxed text-sk-ink/70">Ide konten per tahap funnel — dari awareness sampai closing — biar strategi seimbang.</p>
            </motion.div>

            {/* Rate Card Generator — featured wide (2x1), terracotta, horizontal */}
            <motion.div
              data-testid="feature-card-rate"
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="rounded-2xl p-6 sm:p-7 bg-sk-brand text-sk-ink flex items-center gap-5 min-[520px]:col-span-2"
            >
              <span className="h-14 w-14 shrink-0 rounded-2xl bg-sk-ink/15 flex items-center justify-center">
                <ReceiptText className="h-7 w-7 text-sk-ink" strokeWidth={1.7} />
              </span>
              <div className="min-w-0">
                <h3 className="font-bold text-lg sm:text-xl text-sk-ink">Rate Card Generator</h3>
                <p className="text-sm mt-1.5 leading-relaxed text-sk-ink/85">Susun daftar harga endorse profesional dari data followers & engagement, siap kirim ke brand.</p>
              </div>
            </motion.div>

            {/* Kalender Konten — standard */}
            <motion.div data-testid="feature-card-Kalender Konten" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="rounded-2xl p-6 bg-sk-cream text-sk-ink flex flex-col">
              <CalendarDays className="h-8 w-8 mb-4 text-sk-brand" strokeWidth={1.6} />
              <h3 className="font-bold text-lg text-sk-ink">Kalender Konten</h3>
              <p className="text-sm mt-2 leading-relaxed text-sk-ink/70">Jadwalkan ide & script kamu tiap tanggal biar konten konsisten tanpa pusing.</p>
            </motion.div>
          </div>

          {/* Product proof mockup */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-14 grid lg:grid-cols-2 gap-8 items-center rounded-3xl border border-sk-line bg-sk-surface p-6 sm:p-8">
            <div>
              <Tag>Produk nyata</Tag>
              <h3 className="mt-5 font-extrabold text-2xl sm:text-3xl tracking-tight text-sk-cream">Dari ide mentah jadi script siap tayang.</h3>
              <p className="text-sk-muted mt-3 leading-relaxed">Antarmuka yang bersih dan terarah: pilih DNA brand, generate tiap bagian script, lalu jadwalkan — tanpa ribet, tanpa pindah-pindah tool.</p>
              <button onClick={scrollToPricing} className="mt-6 inline-flex items-center gap-2 rounded-full bg-sk-cream text-sk-ink font-bold px-6 py-3 hover:-translate-y-0.5 transition-transform shadow-lg shadow-black/10">
                <Zap className="h-4 w-4 text-sk-brand" /> Coba Sekarang
              </button>
            </div>
            <div className="relative">
              <img src={IMG.mockup} alt="Tampilan aplikasi Susun Script" loading="lazy" decoding="async" className="relative w-full h-auto rounded-2xl border border-sk-line shadow-xl shadow-black/10" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social proof */}
      <section className="border-b border-sk-line bg-sk-surface2/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <Tag>Kata mereka</Tag>
          <h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream max-w-2xl">Dipakai kreator untuk berkarya lebih konsisten.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.handle} data-testid={`testimonial-${i}`} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-sk-line bg-sk-surface p-6 flex flex-col">
                <div className="flex gap-1 text-sk-brand mb-3">{"★★★★★".split("").map((s, k) => <span key={k}>{s}</span>)}</div>
                <p className="text-sk-cream text-sm leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-5">
                  <img src={t.img} alt={t.name} loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover border border-sk-line" />
                  <div><p className="text-sm font-bold text-sk-cream">{t.name}</p><p className="text-xs text-sk-muted">{t.handle} · Instagram</p></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA — bookend with Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="relative overflow-hidden rounded-3xl border border-sk-line bg-sk-surface" style={dotGrid}>
          <div className="px-6 sm:px-12 py-16 text-center max-w-2xl mx-auto">
            <div className="flex justify-center"><Tag>Siap berkarya</Tag></div>
            <h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Berhenti nunggu mood. Mulai bikin konten hari ini.</h2>
            <p className="text-sk-muted mt-4 leading-relaxed">Satu langganan buka semua fitur. Dari personal brand sampai kalender konten — semua nyambung sama gaya kamu.</p>
            <button onClick={() => subscribe("yearly")} data-testid="cta-subscribe-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-sk-cream text-sk-ink font-bold px-8 py-4 hover:-translate-y-0.5 transition-transform shadow-lg shadow-black/10">
              <Zap className="h-5 w-5 text-sk-brand" /> Mulai Sekarang
            </button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-sk-line scroll-mt-20 bg-sk-bg">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-center flex flex-col items-center">
            <Tag>Harga</Tag>
            <h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Satu langganan, semua fitur terbuka.</h2>
            <p className="text-sk-muted mt-3">Akses penuh ke semua tool kreatif. Batalkan kapan saja.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-12 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-7 flex flex-col">
              <p className="font-bold text-sk-cream text-lg">Bulanan</p>
              <p className="text-sk-muted text-sm mt-1">Fleksibel, bayar per bulan.</p>
              <div className="mt-5 mb-1"><span className="font-extrabold text-4xl text-sk-cream tracking-tight">Rp55.000</span><span className="text-sk-muted text-sm">/bulan</span></div>
              <ul className="space-y-2.5 mt-6 mb-7 flex-1">
                {["Semua fitur terbuka", "DNA Personal Brand tak terbatas", "Library skrip permanen", "Update fitur berkala"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-sk-muted"><Check className="h-4 w-4 text-sk-brand mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>
              <button onClick={() => subscribe("monthly")} data-testid="subscribe-monthly-btn" className="w-full rounded-full border-2 border-sk-line text-sk-cream font-bold py-3 hover:border-sk-brand hover:text-sk-brand transition-colors">Berlangganan Sekarang</button>
            </div>
            <div className="relative rounded-2xl border-2 border-sk-brand/50 bg-sk-surface p-7 flex flex-col">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-sk-brand/60 bg-sk-surface text-xs font-bold text-sk-brand px-3 py-1 whitespace-nowrap">Hemat 2 bulan · Best value</span>
              <p className="font-bold text-sk-cream text-lg">Tahunan</p>
              <p className="text-sk-muted text-sm mt-1">Paling hemat untuk kreator serius.</p>
              <div className="mt-5 mb-1"><span className="font-extrabold text-4xl text-sk-cream tracking-tight">Rp550.000</span><span className="text-sk-muted text-sm">/tahun</span></div>
              <p className="text-xs text-sk-brand font-semibold">≈ Rp45.800/bulan</p>
              <ul className="space-y-2.5 mt-5 mb-7 flex-1">
                {["Semua benefit paket Bulanan", "Hemat setara 2 bulan gratis", "Prioritas fitur baru", "Bebas pusing perpanjang tiap bulan"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-sk-muted"><Check className="h-4 w-4 text-sk-brand mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>
              <button onClick={() => subscribe("yearly")} data-testid="subscribe-yearly-btn" className="w-full rounded-full bg-sk-brand text-sk-ink font-bold py-3 hover:bg-sk-brandDark transition-colors shadow-lg shadow-sk-brand/20">Berlangganan Sekarang</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20 scroll-mt-20">
        <div className="flex flex-col items-center text-center mb-10"><Tag>FAQ</Tag><h2 className="mt-5 font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Pertanyaan umum</h2></div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl border border-sk-line bg-sk-surface overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-${i}`} className="w-full flex items-center justify-between gap-4 p-5 text-left">
                <span className="font-bold text-sk-cream">{f.q}</span>
                <ChevronDown className={`h-5 w-5 text-sk-brand shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-5 text-sm text-sk-muted leading-relaxed -mt-1">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-sk-line bg-sk-surface">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <img src="/logo.png" alt="Studio Kreator" className="h-8 w-auto" />
            <p className="text-sk-muted text-sm mt-3 max-w-sm">AI creative assistant untuk content creator Indonesia — dari ide sampai script siap tayang.</p>
            <p className="text-sk-muted/80 text-xs mt-2">Dijalankan oleh PT. Studio Kreator Indonesia</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/kebijakan-privasi" data-testid="footer-privacy-link" className="text-sk-muted hover:text-sk-brand transition-colors">Kebijakan Privasi</Link>
            <Link to="/syarat-layanan" data-testid="footer-terms-link" className="text-sk-muted hover:text-sk-brand transition-colors">Syarat Layanan</Link>
            <a href="mailto:halo@studiokreator.id" className="text-sk-muted hover:text-sk-brand transition-colors">halo@studiokreator.id</a>
          </div>
        </div>
        <div className="border-t border-sk-line">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-sk-muted">
            <span>© 2026 Studio Kreator. Semua hak dilindungi.</span>
            <span>Dibuat untuk kreator Indonesia 🇮🇩</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
